// (2026-07-02) SENIAT Fase 1: sección "Catálogos SENIAT" — SOLO VISUALIZACIÓN.
// Muestra los 21 catálogos oficiales del documento GGTIC.GIT.00.02 v1.4
// tal como los publica el backend (GET /api/seniat/catalogos). No hay
// botones de crear/editar/borrar ni inputs: la edición de los catálogos
// se realiza directamente en base de datos. Acceso restringido a los
// roles admin y auditor (el backend responde 403 para el resto).
//
// (2026-07-03) Rearmonizado a la identidad visual Sirius del sistema
// (gradiente de marca sirius→spark, tablas con cabecera sirius-50/sirius-700,
// badges sirius-chip/sirius-badge, tipografía Kanit / ink-*). Coherente con
// pages/Compras/ui.js y assets/styles/tailwind.css. Solo cambia la
// presentación; la lógica de carga/acordeón/render defensivo se conserva.
import React, { useState, useEffect } from "react";
import { getCatalogosSeniat } from "../../controllers/apiSeniat";

// Etiqueta legible de una columna del API: 'codigo_seniat' -> 'Código SENIAT'.
// Diccionario para los nombres con acento/estilo propio; el resto se
// capitaliza palabra por palabra.
const COLUMN_LABELS = {
  codigo: "Código",
  codigo_seniat: "Código SENIAT",
  codigo_numerico: "Código numérico",
  descripcion: "Descripción",
  tasa_porcentaje: "Tasa (%)",
  porcentaje: "Porcentaje (%)",
  aplica_igtf: "¿Aplica IGTF?",
  activo: "Estado",
};

// =============================================================================
// (2026-08-05, capa web) PRESENTACIÓN DE COLUMNAS — punto ÚNICO de ajuste.
//
// El backend publica cada catálogo con sus columnas reales (clave `columnas` de
// GET /api/seniat/catalogos). Esta sección decide qué se ENSEÑA y en qué orden;
// no toca el payload ni la capa de integración (los integradores siguen
// recibiendo todas las columnas, `id` incluido — se añadió a los 21 catálogos a
// petición del cliente).
//
// Si mañana hay que ocultar/renombrar/reordenar una columna de la web, SE TOCA
// AQUÍ y en ningún otro sitio.
//
// Los nombres de columna NO están adivinados: salen de los constructores de
// `_vps_fetch_20260623/optimus_invoice/seniat_routes.py` (dict `constructores`,
// ~línea 1435), verificados el 2026-08-05:
//   Cat 5  ['id','codigo','valor']
//   Cat 6  ['id','codigo','nombre','codigo_numerico','decimales','activo']
//   Cat 7  ['id','codigo','descripcion','es_rif']
//   Cat 9  ['id','codigo','nombre','tasa_porcentaje','codigo_seniat']
//   Cat 10 ['id','codigo','nombre','codigo_seniat','aplica_igtf','moneda',
//           'activo','es_referencia_oficial']  (+ la sintética `origen`, que
//           añade `fusionarCatalogo10` en este mismo archivo)
//   Cat 14/15/16 ['id','valor']  ← hoy NO traen `codigo`; el backend lo va a
//           empezar a enviar. La config de abajo funciona en ambos casos: si
//           `codigo` no llega, `orden` simplemente no encuentra nada que mover.
// =============================================================================

// Columnas ocultas en TODOS los catálogos, presentes y futuros.
// `id` es la referencia relacional para la capa de integración; el cliente pidió
// expresamente que NO se vea en ninguna sección de la web. Al ser una regla
// global, ningún catálogo nuevo puede enseñarla por descuido.
const COLUMNAS_OCULTAS_SIEMPRE = ["id"];

// Reglas POR CATÁLOGO. Cada entrada admite:
//   ocultar[]   columnas que no se pintan en la web.
//   orden[]     columnas que van PRIMERO, en este orden; el resto conserva el
//               orden del API.
//   etiquetas{} override de cabecera solo para ese catálogo (COLUMN_LABELS es
//               global a los 21: renombrar `codigo_seniat` allí rompería los
//               catálogos 18 y 19, donde "Código SENIAT" sí es lo correcto).
const CONFIG_COLUMNAS_WEB = {
  // Cat 6 (Monedas ISO 4217): sin `decimales` ni Estado (`activo`).
  6: { ocultar: ["decimales", "activo"] },
  // Cat 9 (Alícuotas IVA): el código interno (`codigo`) se oculta y el
  // `codigo_seniat` pasa a ser la primera columna, rotulada "Código".
  9: {
    ocultar: ["codigo"],
    orden: ["codigo_seniat"],
    etiquetas: { codigo_seniat: "Código" },
  },
  // Cat 10 (Formas de Pago): fuera Origen, Estado (`activo`) y código interno;
  // `codigo_seniat` primero y rotulado "Código". `es_referencia_oficial` ya la
  // descarta `fusionarCatalogo10` antes de llegar aquí.
  10: {
    ocultar: ["origen", "codigo", "activo"],
    orden: ["codigo_seniat"],
    etiquetas: { codigo_seniat: "Código" },
  },
  // Cat 14/15/16 (Tipo / Origen / Destino de producto): `id` ya lo quita la
  // regla global; `codigo` se declara en `orden` para que salga PRIMERO en
  // cuanto el backend lo envíe (hoy estas tablas solo traen ['id','valor'],
  // así que `orden` no encuentra nada que mover y no rompe nada).
  14: { orden: ["codigo"] },
  15: { orden: ["codigo"] },
  16: { orden: ["codigo"] },
};

// Columnas que se pintan para un catálogo, ya filtradas y ordenadas.
// Función PURA y sin JSX: la suite de tests la extrae VERBATIM de este archivo
// junto con los dos mapas de arriba (src/utils/__tests__/columnas_web.test.js).
// `numero` puede ser null/undefined (sub-tablas sin catálogo asociado): en ese
// caso solo aplica la regla global.
const columnasVisibles = (numero, columnas) => {
  const base = Array.isArray(columnas)
    ? columnas.filter((c) => typeof c === "string")
    : [];
  const cfg = CONFIG_COLUMNAS_WEB[Number(numero)] || {};
  const ocultas = COLUMNAS_OCULTAS_SIEMPRE.concat(
    Array.isArray(cfg.ocultar) ? cfg.ocultar : []
  );
  const visibles = base.filter((c) => ocultas.indexOf(c) === -1);
  const primeras = (Array.isArray(cfg.orden) ? cfg.orden : []).filter(
    (c) => visibles.indexOf(c) !== -1
  );
  return primeras.concat(visibles.filter((c) => primeras.indexOf(c) === -1));
};

// Overrides de etiqueta del catálogo indicado (o {} si no tiene).
const etiquetasDe = (numero) => {
  const cfg = CONFIG_COLUMNAS_WEB[Number(numero)] || {};
  return cfg.etiquetas || {};
};

const columnLabel = (col, labels) => {
  if (labels && labels[col]) return labels[col];
  if (COLUMN_LABELS[col]) return COLUMN_LABELS[col];
  return String(col)
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

// (2026-08-03, ronda 2 / T3) Catálogo 10 en UNA sola tabla — ahora el conjunto
// YA VIENE UNIFICADO DEL BACKEND.
//
// El backend (`seniat_routes._cat10_formas_pago`) devuelve TODAS las filas de
// `metodos_pago` —propias y de referencia oficial— en `valores`, con las mismas
// columnas, ordenadas por `codigo_seniat`, y con la bandera
// `es_referencia_oficial` como distintivo de origen. La clave `catalogo_oficial`
// FUE ELIMINADA del payload (decisión del usuario: "objetos separados NO
// aceptables").
//
// Lo que hace esta función, por tanto, es casi la IDENTIDAD:
//   - Deriva la columna sintética `origen` ('Oficial' | 'Propio') de
//     `es_referencia_oficial` y la coloca PRIMERO.
//   - Oculta la columna cruda `es_referencia_oficial`: `origen` ya la muestra,
//     y repetir el mismo dato en dos columnas es ruido.
//   - Conserva el ORDEN que manda el backend (no reordena: el criterio
//     normativo por `codigo_seniat` ya se aplicó allí).
//
// COMPATIBILIDAD (deliberada, no adorno): si llegara un payload ANTIGUO —con
// `catalogo_oficial` y sin oficiales dentro de `valores`— sus filas se añaden
// mapeadas a las mismas columnas, PERO SOLO las que no estén ya presentes en
// `valores` (dedupe por `codigo_seniat`). Esa guarda es lo que impide que un
// bundle nuevo servido contra un backend nuevo pinte las oficiales DOS veces si
// alguna vez volviera a viajar la sub-tabla. Sin dedupe, la fusión duplicaría.
//
// (2026-08-05) OJO: `origen` sigue derivándose y sigue yendo PRIMERA en el
// resultado de esta función —es su contrato, verificado por
// proformas_calculos.test.js—, pero YA NO SE PINTA: la capa web la oculta en
// CONFIG_COLUMNAS_WEB[10].ocultar. Presentación y contrato de datos son cosas
// distintas a propósito; el filtrado vive en un solo sitio (TablaCatalogo).
//
// Función PURA, sin JSX y SIN DEPENDENCIAS EXTERNAS a propósito: la suite de
// tests extrae su cuerpo VERBATIM de este archivo y lo evalúa aislado
// (src/utils/__tests__/proformas_calculos.test.js). Cualquier constante de
// módulo que se use aquí dentro rompería esa extracción con un ReferenceError,
// por eso 'Oficial'/'Propio' van como literales.
const fusionarCatalogo10 = (cat) => {
  const columnasBase =
    Array.isArray(cat && cat.columnas) && cat.columnas.length > 0
      ? cat.columnas.slice()
      : ["codigo", "nombre", "codigo_seniat", "aplica_igtf", "moneda", "activo"];
  const columnas = ["origen"].concat(
    columnasBase.filter((c) => c !== "origen" && c !== "es_referencia_oficial")
  );

  // (1) El conjunto unificado del backend, tal cual llega.
  const filas = (Array.isArray(cat && cat.valores) ? cat.valores : [])
    .filter((f) => f && typeof f === "object")
    .map((f) =>
      Object.assign({}, f, {
        origen: f.es_referencia_oficial ? "Oficial" : "Propio",
      })
    );

  // (2) Retrocompatibilidad: sub-tabla `catalogo_oficial` de un backend viejo.
  const yaPresentes = {};
  filas.forEach((f) => {
    if (f.codigo_seniat != null) yaPresentes[String(f.codigo_seniat)] = true;
  });
  const comoFilaOficial = (codigo, descripcion) => ({
    origen: "Oficial",
    codigo: null,
    nombre: descripcion != null ? descripcion : null,
    codigo_seniat: codigo != null ? codigo : null,
    aplica_igtf: null,
    moneda: null,
    activo: null,
  });

  const oficialesRaw = cat && cat.catalogo_oficial;
  let legado = [];
  if (Array.isArray(oficialesRaw)) {
    legado = oficialesRaw
      .filter((f) => f && typeof f === "object")
      .map((f) => comoFilaOficial(f.codigo, f.descripcion));
  } else if (oficialesRaw && typeof oficialesRaw === "object") {
    // Forma alternativa tolerada históricamente: mapa {codigo: descripcion}.
    legado = Object.keys(oficialesRaw).map((codigo) =>
      comoFilaOficial(codigo, oficialesRaw[codigo])
    );
  }
  legado = legado.filter(
    (f) => f.codigo_seniat == null || !yaPresentes[String(f.codigo_seniat)]
  );

  return { columnas: columnas, valores: legado.concat(filas) };
};

// Formato canónico del proyecto para booleanos en listas (estilo columna
// "Estado": círculo verde/rojo). Para la columna 'activo' se usa
// Activo/Inactivo (badge del catálogo 6 — monedas); para el resto Si/No.
const BoolBadge = ({ value, col }) => {
  const esActivo = col === "activo";
  const textoSi = esActivo ? "Activo" : "Si";
  const textoNo = esActivo ? "Inactivo" : "No";
  return value === true ? (
    <span className="whitespace-nowrap">
      <i className="fas fa-circle text-emerald-500 mr-2" aria-hidden="true"></i>
      {textoSi}
    </span>
  ) : (
    <span className="whitespace-nowrap">
      <i className="fas fa-circle text-red-500 mr-2" aria-hidden="true"></i>
      {textoNo}
    </span>
  );
};

// (2026-08-05, capa web) `OrigenBadge` ELIMINADO — decisión explícita, no
// descuido. El cliente pidió ocultar la columna "Origen" del Catálogo 10
// (CONFIG_COLUMNAS_WEB[10].ocultar), que era su ÚNICO consumidor: mantenerlo
// sería código muerto. La derivación de `origen` SÍ se conserva dentro de
// `fusionarCatalogo10` porque forma parte del contrato de esa función —
// retrocompatibilidad y dedupe del payload antiguo, cubierto por
// src/utils/__tests__/proformas_calculos.test.js. Si alguna vez se volviera a
// mostrar la columna, se pintaría como texto plano ('Oficial' / 'Propio') hasta
// que se reponga un distintivo.

// Render de una celda según el tipo del valor (defensivo: el API define
// las columnas dinámicamente por catálogo).
const renderCelda = (valor, col) => {
  if (valor === null || valor === undefined || valor === "") return "—";
  if (typeof valor === "boolean") return <BoolBadge value={valor} col={col} />;
  if (typeof valor === "object") return JSON.stringify(valor);
  return String(valor);
};

// Tabla genérica de un catálogo: columnas dinámicas según 'columnas' del API.
// 'valores' llega como lista de objetos; si una fila llegara como valor
// simple (string/número) se muestra en una sola celda a lo ancho.
// Estilo Sirius: cabecera sirius-50/sirius-700 uppercase, filas con hover
// sirius, bordes slate suaves (coherente con pages/Compras/ui.js Th/Td).
// PUNTO ÚNICO de aplicación de la presentación de columnas: TODA tabla de esta
// pantalla pasa por aquí, así que `columnasVisibles` se invoca una sola vez y
// ningún catálogo puede saltarse la regla (`id` oculto, ocultos por catálogo,
// orden). `numero` puede faltar (sub-tabla normativa): solo aplican las reglas
// globales.
function TablaCatalogo({ numero, columnas, valores }) {
  const cols = columnasVisibles(numero, columnas);
  const labels = etiquetasDe(numero);
  const filas = Array.isArray(valores) ? valores : [];

  if (filas.length === 0) {
    return (
      <p className="text-xs text-ink-400 italic py-3 px-2">
        Sin valores registrados para este catálogo.
      </p>
    );
  }

  // Todas las columnas del catálogo están ocultas en la web: no se pinta una
  // tabla sin cabeceras ni celdas (quedaría un bloque vacío indescifrable).
  if (cols.length === 0) {
    return (
      <p className="text-xs text-ink-400 italic py-3 px-2">
        Este catálogo no tiene columnas visibles en la web.
      </p>
    );
  }

  return (
    <div className="block w-full overflow-x-auto rounded-xl border border-slate-200">
      <table className="items-center w-full bg-transparent border-collapse">
        <thead>
          <tr className="bg-sirius-50">
            {cols.map((col) => (
              <th
                key={col}
                className="px-4 py-3 text-left text-[11px] uppercase tracking-wider font-semibold text-sirius-700 whitespace-nowrap"
              >
                {columnLabel(col, labels)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {filas.map((fila, idx) => {
            // Fila defensiva: valor simple => una sola celda a lo ancho.
            if (fila === null || typeof fila !== "object") {
              return (
                <tr
                  key={idx}
                  className="border-t border-slate-100 hover:bg-sirius-50/40 transition-colors"
                >
                  <td
                    className="px-4 py-2.5 text-sm text-ink-700"
                    colSpan={cols.length || 1}
                  >
                    {renderCelda(fila)}
                  </td>
                </tr>
              );
            }
            return (
              <tr
                key={idx}
                className="border-t border-slate-100 hover:bg-sirius-50/40 transition-colors"
              >
                {cols.map((col) => (
                  <td key={col} className="px-4 py-2.5 text-sm text-ink-700">
                    {renderCelda(fila[col], col)}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// Sub-lista normativa del catálogo 10 (formas de pago 01-18/99 del
// documento SENIAT). Puede llegar como lista de objetos o como mapa
// {codigo: descripcion}; se renderiza defensivamente en ambos casos.
function CatalogoOficial({ data }) {
  if (!data) return null;

  let columnas;
  let valores;
  if (Array.isArray(data)) {
    columnas =
      data.length > 0 && typeof data[0] === "object" && data[0] !== null
        ? Object.keys(data[0])
        : [];
    valores = data;
  } else if (typeof data === "object") {
    columnas = ["codigo", "descripcion"];
    valores = Object.entries(data).map(([codigo, descripcion]) => ({
      codigo,
      descripcion,
    }));
  } else {
    return null;
  }

  return (
    <div className="mt-4">
      <p className="text-[11px] uppercase tracking-[0.12em] font-semibold text-ink-500 mb-2 px-1">
        Catálogo oficial SENIAT (tabla normativa)
      </p>
      <TablaCatalogo columnas={columnas} valores={valores} />
    </div>
  );
}

// (2026-08-05, ENCARGO DEL CLIENTE, verbatim: «De los servicios de CAT remover
// los atributos (fuente y nota)».) Aquí vivían el diccionario de distintivos de
// origen y el componente que los pintaba en la cabecera de cada catálogo.
//
// Se ELIMINAN, no se dejan a la espera: el API ya no envía el atributo, así que
// el badge no tendría de dónde saber el origen. Ante un valor ausente el
// componente caía a "Normativo" — es decir, con el contrato nuevo habría
// afirmado en TODOS los catálogos que se sirven desde la norma, que es
// exactamente el defecto de badge MENTIROSO que se corrigió el 2026-08-02
// (entonces "todo lo que no fuera bd se pintaba Normativo", y una tabla vacía
// por avería se leía como "así lo dice el SENIAT"). Un distintivo que ya no
// puede saber el origen no debe adivinarlo.
//
// Qué le queda al usuario para entender una tabla sin filas: el mensaje
// "Sin valores registrados para este catálogo" de `TablaCatalogo`, que se
// CONSERVA. Junto a él, el bloque de error de carga de la pantalla completa
// (`error`) sigue cubriendo el fallo de la petición.

function CatalogosSeniat() {
  const [catalogos, setCatalogos] = useState([]);
  const [documento, setDocumento] = useState("GGTIC.GIT.00.02 v1.4");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [forbidden, setForbidden] = useState(false);
  // Acordeón: número del catálogo expandido (null = todos colapsados).
  const [abierto, setAbierto] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await getCatalogosSeniat();
        setCatalogos(Array.isArray(data?.catalogos) ? data.catalogos : []);
        if (data?.documento) setDocumento(data.documento);
      } catch (err) {
        console.error(err);
        if (err.response?.status === 403) {
          setForbidden(true);
        } else {
          setError(
            err.response?.data?.error || "Error al cargar los catálogos SENIAT"
          );
        }
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const toggleCatalogo = (numero) => {
    setAbierto((prev) => (prev === numero ? null : numero));
  };

  return (
    <div className="mx-auto w-full">
      <div className="flex flex-wrap">
        <div className="w-full lg:w-12/12">
          <div className="relative bg-white flex flex-col min-w-0 break-words w-full mb-6 shadow-lg rounded-lg bg-blueGray-100 border-0">
            {/* Header — patrón PageHeader Sirius (icono de marca + subtítulo +
                contador a la derecha). */}
            <div className="rounded-t bg-white mb-0 px-6 py-6 flex justify-between items-center border-b">
              <div>
                <h6 className="text-blueGray-700 text-xl font-bold flex items-center gap-2">
                  <i
                    className="fas fa-book-open text-sirius-600"
                    aria-hidden="true"
                  />
                  Catálogos SENIAT
                </h6>
                {/* (2026-08-05, capa web) Se retiró la coletilla "— la edición
                    se realiza directamente en base de datos": el cliente pidió
                    que ese concepto no se le muestre al usuario en ninguna
                    parte de la pantalla. Lo útil del subtítulo (el documento
                    normativo y el aviso de solo lectura) SE CONSERVA, así que
                    el elemento no se elimina y el encabezado mantiene su
                    espaciado (mt-1) sin quedar hueco. */}
                <p className="text-blueGray-400 text-sm mt-1">
                  {documento} · solo visualización
                </p>
              </div>
              {!loading && !forbidden && !error && catalogos.length > 0 && (
                <span className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-semibold uppercase tracking-wider bg-sirius-50 text-sirius-700 border border-sirius-100">
                  <i className="fas fa-layer-group" aria-hidden="true" />
                  {catalogos.length} catálogos
                </span>
              )}
            </div>

            {/* Contenido */}
            <div className="block w-full px-4 py-4">
              {loading && (
                <div className="flex items-center gap-3 text-sm text-ink-500 py-6 px-2">
                  <i className="fas fa-circle-notch fa-spin text-sirius-500" />
                  Cargando catálogos SENIAT…
                </div>
              )}

              {!loading && forbidden && (
                <div className="flex items-center gap-3 text-sm text-red-600 bg-red-50 border border-red-100 rounded-xl py-4 px-4">
                  <i className="fas fa-lock" aria-hidden="true" />
                  Sección disponible solo para administradores y auditores.
                </div>
              )}

              {!loading && !forbidden && error && (
                <div className="flex items-center gap-3 text-sm text-red-600 bg-red-50 border border-red-100 rounded-xl py-4 px-4">
                  <i className="fas fa-triangle-exclamation" aria-hidden="true" />
                  {error}
                </div>
              )}

              {!loading && !forbidden && !error && (
                <ul className="space-y-2.5">
                  {catalogos.map((cat) => {
                    const expandido = abierto === cat.numero;
                    return (
                      <li
                        key={cat.numero}
                        className={`border rounded-xl overflow-hidden transition-shadow ${
                          expandido
                            ? "border-sirius-200 shadow-sirius"
                            : "border-slate-200 hover:shadow-sm"
                        }`}
                      >
                        {/* Cabecera del acordeón (solo expandir/colapsar) */}
                        <button
                          type="button"
                          onClick={() => toggleCatalogo(cat.numero)}
                          className={`w-full flex items-center justify-between px-4 py-3 text-left transition-colors ${
                            expandido
                              ? "bg-sirius-50/70"
                              : "bg-white hover:bg-sirius-50/40"
                          }`}
                          aria-expanded={expandido}
                        >
                          <span className="flex items-center gap-3 min-w-0">
                            <span className="shrink-0 w-9 h-9 rounded-xl bg-gradient-to-br from-sirius-600 to-spark-500 text-white text-xs font-display font-bold flex items-center justify-center shadow-spark">
                              {cat.numero}
                            </span>
                            <span className="text-ink-900 text-sm font-display font-bold truncate">
                              {cat.nombre}
                            </span>
                          </span>
                          <i
                            className={`fas fa-chevron-down text-xs text-sirius-400 transition-transform duration-200 ${
                              expandido ? "rotate-180" : ""
                            }`}
                            aria-hidden="true"
                          />
                        </button>

                        {/* Cuerpo: tabla de valores del catálogo */}
                        {expandido && (
                          <div className="bg-white border-t border-slate-200 px-3 py-4">
                            {/* (2026-08-05, encargo cliente) Aquí se pintaba
                                `cat.nota`, el texto explicativo que el API
                                enviaba por catálogo. El atributo se retiró del
                                servicio, así que el párrafo (y su icono) se
                                eliminan enteros: no queda contenedor vacío ni
                                margen suelto — la tabla es ahora el primer
                                elemento del cuerpo del acordeón. */}
                            {/* (2026-08-02, ítem 24-b) Catálogo 10 UNIFICADO:
                                oficiales + propios en UNA sola tabla.
                                (2026-08-05) La columna "Origen" que fusiona
                                esa función ya NO se pinta en la web: la oculta
                                CONFIG_COLUMNAS_WEB[10], igual que Estado, Id y
                                el código interno. El resto de catálogos se
                                renderiza exactamente igual que antes. */}
                            {Number(cat.numero) === 10 ? (
                              (() => {
                                const fusion = fusionarCatalogo10(cat);
                                return (
                                  <TablaCatalogo
                                    numero={10}
                                    columnas={fusion.columnas}
                                    valores={fusion.valores}
                                  />
                                );
                              })()
                            ) : (
                              <>
                                <TablaCatalogo
                                  numero={cat.numero}
                                  columnas={cat.columnas}
                                  valores={cat.valores}
                                />
                                {/* Sub-tabla normativa (si algún otro catálogo
                                    llegara a traerla). El 10 ya la fusiona. */}
                                <CatalogoOficial data={cat.catalogo_oficial} />
                              </>
                            )}
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CatalogosSeniat;
